import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import {
	IFormHandler,
	useFormHandler
} from "client/Forms";
import {
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { getFileNameWithoutExtension } from "client_legacy/Documents/path";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useState } from "react";

export type RenameLinkedDocumentDialogProps = {
	initialName: string;
	onClose(): void;
	onRenamed(
		newName: string
	): void;
};

export const RenameLinkedDocumentDialog = (props: RenameLinkedDocumentDialogProps): ReactElement => {
	const {
		initialName,
		onClose,
		onRenamed
	} = props;

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [model, setModel] = useState<RenameLinkedDocumentFormModel>(
		() => ({
			name: getFileNameWithoutExtension(initialName)
		})
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<RenameLinkedDocumentFormModel> = useFormHandler(
		model,
		setModel,
		validationConfiguration
	);

	const submitHandler = async(): Promise<void> => {
		if (isSubmitting) {
			return;
		}
		setIsSubmitting(true);

		const validationResult: InputValidationResult = await formHandler.validation
			.validate(globalCancellation);

		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsSubmitting(false);
			return;
		}

		onRenamed(model.name);
	};

	return (
		<Dialog
			trackingId="rename-linked-document-dialog"
			hasBusyIndicator={isSubmitting}
		>
			<Dialog.Header
				title={localize("_RenameLink")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form
					onSubmit={submitHandler}
				>
					<FormItem>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
							autoFocus="focus"
						/>
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={submitHandler}
						text={localize("_Rename")}
						trackingId="rename"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
RenameLinkedDocumentDialog.displayName = "RenameLinkedDocumentDialog";

type RenameLinkedDocumentFormModel = {
	name: string;
};

const validationConfiguration: IValidationConfiguration<RenameLinkedDocumentFormModel> =
	createValidationConfiguration<RenameLinkedDocumentFormModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);